import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import logo from './tbd-logo.webp';
import FaultyTerminal from './FaultyTerminal';
import MobileMenu from './MobileMenu';
import Home from './Home';
import About from './About';
import Contact from './Contact';
import NotFound from './NotFound';
import Sonic from './Sonic';
import './App.css';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
  ('ontouchstart' in window && window.innerWidth < 1024);

// Mobile video background
function MobileBackground() {
  return (
    <video
      autoPlay
      muted
      loop
      playsInline
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        pointerEvents: 'none',
        zIndex: 1,
        opacity: 0.4,
      }}
    >
      <source src="/Video.mp4" type="video/mp4" />
    </video>
  );
}

// Navigation Component with Mobile Menu
function Navigation() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header className="App-navbar">
        <div className="navbar-brand">
          <Link to="/" className="magnetic-element">
            <img src={logo} className="App-logo" alt="logo" />
          </Link>
          <span>Trailblaze Digital</span>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          <Link 
            to="/about" 
            className={`nav-link magnetic-element ${location.pathname === '/about' ? 'active' : ''}`}
          >
            Our Story
          </Link>
          <Link 
            to="/contact" 
            className={`nav-link magnetic-element ${location.pathname === '/contact' ? 'active' : ''}`}
          >
            Contact Us
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-button"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </header>

      <MobileMenu isOpen={isMobileMenuOpen} onClose={closeMobileMenu} />
    </>
  );
}

// Footer Component with Secret Button
function Footer() {
  const [clickCount, setClickCount] = useState(0);
  const navigate = useNavigate();

  const handleSecretClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount === 5) {
      navigate('/sonic');
      setClickCount(0); // Reset counter
    }
  };

  return (
    <footer>
      <div className="footer-content">
        <div className="footer-main">
          <div className="footer-info">
            <div className="company-info">
              <span className="copyright">© 2026 Trailblaze Digital. All rights reserved.</span>
              <div className="contact-details">
                <div className="contact-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor"/>
                  </svg>
                  <a href="mailto:info@trailblazedigital.net">info@trailblazedigital.net</a>
                </div>
                <div className="contact-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.82 16.43 14.93C17.55 15.3 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" fill="currentColor"/>
                  </svg>
                  <a href="tel:+905488342265">+971 50 342 4890</a>
                </div>
                <div className="contact-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9S10.62 6.5 12 6.5S14.5 7.62 14.5 9S13.38 11.5 12 11.5Z" fill="currentColor"/>
                  </svg>
                  <span>United Arab Emirates</span>
                </div>
              </div>
            </div>
            <button
              className="secret-button"
              onClick={handleSecretClick}
              aria-label="Secret button"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="3" fill="currentColor"/>
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Main App Component
function App() {
  return (
    <Router>
      <div className="App">
        {isMobile ? (
          <MobileBackground />
        ) : (
          <FaultyTerminal 
            scale={3}
            gridMul={[2, 1]}
            digitSize={1.8}
            timeScale={0.4}
            scanlineIntensity={0.4}
            glitchAmount={1.2}
            flickerAmount={0.8}
            noiseAmp={0.5}
            chromaticAberration={2}
            dither={0.3}
            curvature={0.1}
            tint="#ffffff"
            mouseReact={true}
            mouseStrength={0.3}
            brightness={0.8}
            pageLoadAnimation={true}
          />
        )}
        <Navigation />  
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/sonic" element={<Sonic />} />
          <Route path="*" element={<NotFound />} /> 
        </Routes>
        <Footer />
      </div>
    </Router>
  );
}

export default App;